import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LucideIconsModule } from '../../../../shared/lucide-icons.module';
import { PropertyUploadService, LookupRecord } from '../../../../core/services/property-upload.service';
import { PropertiesService } from '../../../../core/services/properties.service';
import { NotificationService } from '../../../../services/notification.service';
import { Project, Phase } from '../../../../core/models/project.model';
import { ConfirmDialogService } from '../../../../shared/components/confirm-dialog/confirm-dialog.service';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IS_DEV_HOST } from '../../../../core/config/api.config';
import { PROPERTY_TYPE_OPTIONS } from '../../../../shared/data/property-types';
import { buildWorkspaceRouteUrl } from '../../../../shared/utils/media-library-popup.util';
import { PropertyCreateWizardComponent } from './components/property-create-wizard/property-create-wizard.component';

const COUNTRY_CURRENCY_MAP: Record<string, string> = {
    QA: 'QAR',
    AE: 'AED',
    SA: 'SAR',
    US: 'USD',
    GB: 'GBP',
    EG: 'EGP',
    FR: 'EUR',
    DE: 'EUR',
    IT: 'EUR',
    ES: 'EUR',
    CA: 'USD',
    AU: 'USD',
    PK: 'PKR',
    IN: 'INR',
    BD: 'BDT',
    TR: 'TRY',
    KW: 'KWD',
    BH: 'BHD',
    OM: 'OMR',
    JO: 'JOD'
};

const LANDMARK_PRESETS = ['Mall', 'School', 'University', 'Hospital', 'Clinic', 'Metro Station', 'Airport', 'Park', 'Beach', 'Downtown'];

type StepState = 'done' | 'active' | 'pending';

interface FeatureOption {
    id: string;
    name: string;
    dataTypeLabel?: string;
}

interface UploadedImage {
    file: File;
    preview: string;
    description: string;
    isMain: boolean;
}

type PropertyTypeSelectOption = { label: string; value: string; disabled?: boolean };

type PropertyCreateDraftV1 = {
    v: 1;
    saved_at: string;
    current_step: number;
    created_property_id: string | null;
    plan_mode: 'manual' | 'auto' | 'none';
    public_price_source?: string;
    plan_generator?: any;
    property_form: any;
    selected_features: Array<[string, string]>;
    landmarks: any[];
    payment_plans: any[];
};

@Component({
    selector: 'app-property-create',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, LucideIconsModule, PropertyCreateWizardComponent],
    templateUrl: './property-create.component.html',
    styleUrls: ['./property-create.component.scss']
})
export class PropertyCreateComponent implements OnInit, OnDestroy {
    private fb = inject(FormBuilder);
    private router = inject(Router);
    private uploadService = inject(PropertyUploadService);
    private propertiesService = inject(PropertiesService);
    private route = inject(ActivatedRoute);
    private notifications = inject(NotificationService);
    private confirm = inject(ConfirmDialogService);
    private readonly destroy$ = new Subject<void>();
    private destroyed = false;
    private pendingDraft: PropertyCreateDraftV1 | null = null;
    private readonly draftKey = 'nibni_property_create_draft_v1';
    private draftTouched = false;
    private draftRestoreHandled = false;
    readonly view = this;
    hasSavedDraft = false;

    paymentPlanManagerHref(): string {
        const params = new URLSearchParams();
        if (this.createdPropertyId) {
            params.set('scope_type', 'property');
            params.set('property_id', this.createdPropertyId);
        } else {
            const projectId = String(this.propertyForm.get('projectId')?.value || '').trim();
            if (projectId) {
                params.set('scope_type', 'project');
                params.set('project_id', projectId);
            } else {
                params.set('scope_type', 'org_all_properties');
            }
        }
        return buildWorkspaceRouteUrl('developer', 'payment-plans', params);
    }

    steps = [
        { id: 1, label: 'Info', description: 'Basic details' },
        { id: 2, label: 'Features', description: 'Property features' },
        { id: 3, label: 'Landmarks', description: 'Points of interest' },
        { id: 4, label: 'Images', description: 'Upload photos' },
        { id: 5, label: 'Payment', description: 'Financing' },
        { id: 6, label: 'Complete', description: 'Review' }
    ];

    propertyForm: FormGroup = this.fb.group({
        title: ['', Validators.required],
        description: [''],
        tagLine: [''],
        projectId: ['', Validators.required],
        phaseId: ['', Validators.required],
        country: ['', Validators.required],
        status: ['Available', Validators.required],
        price: ['', [Validators.required, Validators.min(0.000001)]],
        currencyId: ['', Validators.required],
        buaEnabled: [true],
        bua: ['', [Validators.required, Validators.min(0.000001)]],
        buaUnit: ['m²'],
        totalAreaEnabled: [false],
        totalArea: [''],
        totalAreaUnit: ['m²'],
        gardenAreaEnabled: [false],
        gardenArea: [''],
        gardenAreaUnit: ['m²'],
        balconyAreaEnabled: [false],
        balconyArea: [''],
        balconyAreaUnit: ['m²'],
        terraceAreaEnabled: [false],
        terraceArea: [''],
        terraceAreaUnit: ['m²'],
        bedrooms: [''],
        bathrooms: [''],
        numberOfFloors: [''],
        floor: [''],
        unitNumber: ['', Validators.required],
        readyToMove: [''],
        finishingType: [''],
        furnishType: [''],
        cardBadge1: [''],
        cardBadge2: [''],
        discountRateChoice: [''],
        customDiscountRate: [''],
        maintenanceFeeMode: ['fixed'],
        maintenanceFees: [''],
        deliveryDate: [''],
	        categoryId: ['', Validators.required],
	        locationAddress: [''],
	        locationLink: [''],
	        useProjectLocation: [true]
	    });

    landmarksForm: FormGroup = this.fb.group({
        items: this.fb.array([this.createLandmarkGroup()])
    });

    paymentPlansForm: FormGroup = this.fb.group({
        plans: this.fb.array([])
    });

    currentStep = 1;
    createdPropertyId: string | null = null;
    saving = false;
    completed = false;
    error: string | null = null;
    validationSummary: Array<{ stepId: number; label: string; fields: string[] }> = [];
    canEditStatus = false;

    projects: Project[] = [];
    allPhases: Phase[] = [];
    phases: Phase[] = [];
    categories: LookupRecord[] = [];
    propertyTypeSelectOptions: PropertyTypeSelectOption[] = this.buildPropertyTypeSelectOptions([]);
    currencies: LookupRecord[] = [];
    availableFeatures: FeatureOption[] = [];
    selectedFeatures = new Map<string, string>();
    images: UploadedImage[] = [];
    floorPlanFile: File | null = null;
    floorPlanError: string | null = null;
    layoutImageFile: File | null = null;
    layoutImageError: string | null = null;
    presetProjectId: string | null = null;
    isDevHost = IS_DEV_HOST;
    bedroomOptions: Array<number | string> = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, '10+'];
    unitFloorOptions: Array<string | number> = ['Ground', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, '20+', 'Penthouse', 'Basement'];
    numberOfFloorsOptions: Array<string | number> = [
        'Ground',
        'Ground + 1',
        'Ground + 2',
        'Ground + 3',
        'Ground + 4',
        'Ground + 5',
        'Ground + 6',
        'Ground + 7',
        'Ground + 8',
        'Ground + 9',
        'Ground + 10',
        1,
        2,
        3,
        4,
        5,
        6,
        7,
        8,
        9,
        10,
        12,
        15,
        20,
        '20+'
    ];
    furnishOptions = ['Unfurnished', 'Semi-furnished', 'Fully furnished', 'Furnished on request'];
    readonly unitBadgeSecondaryOptions = ['Garden', 'Roof', 'Terrace'];
    finishingOptions = ['Core & Shell', 'Semi-Finished', 'Fully Finished', 'Luxury Finished'];
	    areaUnits = ['m²', 'ft²'];
		    distanceUnits = [
		        { value: 'm', label: 'm' },
		        { value: 'km', label: 'km' },
		        { value: 'mile', label: 'mile' }
		    ];
    landmarkPresets = LANDMARK_PRESETS;
    discountOptions: Array<number | 'custom'> = [0, 5, 10, 15, 20, 25, 30, 'custom'];
	    maintenanceModes = [
	        { value: 'fixed', label: 'Fixed amount' },
	        { value: 'percent', label: 'Percent of price' }
	    ];
	    planMode: 'manual' | 'auto' | 'none' = 'none';
    publicPriceSource = 'static';
	    planGeneratorForm = this.fb.group({
	        scenarios: this.fb.array([this.createPlanScenarioGroup()])
	    });
	    private currencyChangedByUser = false;
	    private updatingCurrencyFromCountry = false;

	    get planScenarios(): FormArray {
	        return this.planGeneratorForm.get('scenarios') as FormArray;
	    }

    ngOnInit(): void {
        this.pendingDraft = this.readDraft();
        this.hasSavedDraft = Boolean(this.pendingDraft);
        this.presetProjectId = this.route.snapshot.queryParamMap.get('projectId');
        this.propertyForm.get('categoryId')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.syncUnitNumberValidation());
        this.syncUnitNumberValidation();
	        this.propertyForm.get('currencyId')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
	            if (!this.updatingCurrencyFromCountry) this.currencyChangedByUser = true;
	        });
		        this.propertyForm.get('country')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((code) => this.applyCountryCurrency(code));
		        this.propertyForm.get('useProjectLocation')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
		            this.syncLocationControls(Boolean(value));
		        });
		        this.propertyForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.touchDraft());
		        this.landmarksForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.touchDraft());
	        this.paymentPlansForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.touchDraft());
	        this.planGeneratorForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.touchDraft());
	        this.initAreaToggleWatchers();
	        this.syncLocationControls(this.useProjectLocation);
	        this.loadLookups();
	    }

    get useProjectLocation(): boolean {
        return Boolean(this.propertyForm.get('useProjectLocation')?.value);
    }

    setUseProjectLocation(value: boolean): void {
        this.propertyForm.patchValue({ useProjectLocation: value }, { emitEvent: false });
        this.syncLocationControls(value, { prefillCustom: true });
        this.touchDraft();
    }

    get selectedProject(): Project | null {
        const projectId = String(this.propertyForm.value.projectId || '').trim();
        if (!projectId) return null;
        return this.projects.find((p) => String(p?.id || '') === projectId) || null;
    }

    get projectLocationAddress(): string | null {
        const address = this.selectedProject?.locationAddress ?? null;
        return address !== null && address !== undefined && String(address).trim() ? String(address).trim() : null;
    }

    get projectLocationLink(): string | null {
        const link = this.selectedProject?.locationLink ?? null;
        return link !== null && link !== undefined && String(link).trim() ? String(link).trim() : null;
    }

    private syncLocationControls(useProjectLocation: boolean, opts: { prefillCustom?: boolean } = {}): void {
        const address = this.propertyForm.get('locationAddress');
        const link = this.propertyForm.get('locationLink');
        if (!address || !link) return;

        if (useProjectLocation) {
            address.disable({ emitEvent: false });
            link.disable({ emitEvent: false });
            address.patchValue('', { emitEvent: false });
            link.patchValue('', { emitEvent: false });
            return;
        }

        address.enable({ emitEvent: false });
        link.enable({ emitEvent: false });

        if (opts.prefillCustom) {
            const currentAddress = String(address.value || '').trim();
            const currentLink = String(link.value || '').trim();
            if (!currentAddress && this.projectLocationAddress) {
                address.patchValue(this.projectLocationAddress, { emitEvent: false });
            }
            if (!currentLink && this.projectLocationLink) {
                link.patchValue(this.projectLocationLink, { emitEvent: false });
            }
        }
    }

    get landmarks(): FormArray {
        return this.landmarksForm.get('items') as FormArray;
    }

    get paymentPlans(): FormArray {
        return this.paymentPlansForm.get('plans') as FormArray;
    }

    get draftInlinePaymentPlans(): Array<Record<string, any>> {
        const plans = (this.paymentPlans.getRawValue?.() as Array<Record<string, any>>) || [];
        return plans.filter((plan) => this.paymentPlanHasValues(plan));
    }

    get floorSelectOptions(): Array<string | number> {
        return this.selectOptionsWithCurrent(this.propertyForm.value.floor, this.unitFloorOptions);
    }

    get numberOfFloorsSelectOptions(): Array<string | number> {
        return this.selectOptionsWithCurrent(this.propertyForm.value.numberOfFloors, this.numberOfFloorsOptions);
    }

    private paymentPlanHasValues(plan: any): boolean {
        if (!plan || typeof plan !== 'object') return false;
        const extraRows = Array.isArray(plan.extraRows) ? plan.extraRows : [];
        const hasExtraRowValue = extraRows.some((row: any) => {
            if (!row || typeof row !== 'object') return false;
            return Boolean(String(row.label || '').trim() || String(row.percent || '').trim());
        });
        return Boolean(
            String(plan.totalPeriod || '').trim()
            || String(plan.paymentCycle || '').trim()
            || String(plan.downPayment || '').trim()
            || String(plan.reservationAmount || '').trim()
            || String(plan.installmentAmount || '').trim()
            || String(plan.numberOfInstallments || '').trim()
            || hasExtraRowValue
        );
    }

    get unitBadgePrimaryOptions(): string[] {
        const current = this.unitBadgePrimaryValue;
        return current && !this.furnishOptions.includes(current) ? [current, ...this.furnishOptions] : this.furnishOptions;
    }

    get unitBadgePrimaryValue(): string {
        return this.resolveUnitBadgeState().primary;
    }

    get unitBadgeSecondaryLimit(): number {
        return this.maxUnitSecondaryBadges(this.unitBadgePrimaryValue);
    }

    isUnitSecondaryBadgeSelected(option: string): boolean {
        return this.resolveUnitBadgeState().secondary.includes(option);
    }

    isUnitSecondaryBadgeDisabled(option: string): boolean {
        const state = this.resolveUnitBadgeState();
        return !state.secondary.includes(option) && state.secondary.length >= this.maxUnitSecondaryBadges(state.primary);
    }

    onUnitBadgePrimaryChange(value: string): void {
        const state = this.resolveUnitBadgeState();
        this.applyUnitBadgeSelection(value, state.secondary);
    }

    onUnitSecondaryBadgeToggle(option: string, checked: boolean): void {
        const state = this.resolveUnitBadgeState();
        const normalized = this.normalizeUnitSecondaryBadge(option);
        if (!normalized) return;
        const next = state.secondary.filter((item) => item !== normalized);
        if (checked) next.push(normalized);
        this.applyUnitBadgeSelection(state.primary, next);
    }

    private selectOptionsWithCurrent(value: unknown, options: Array<string | number>): Array<string | number> {
        const v = value === null || value === undefined ? '' : String(value).trim();
        if (!v) return options;
        const exists = options.some(o => String(o).trim() === v);
        return exists ? options : [v, ...options];
    }

    private resolveUnitBadgeState(): { primary: string; secondary: string[] } {
        const badge1 = String(this.propertyForm.get('cardBadge1')?.value || '').trim();
        const badge2 = String(this.propertyForm.get('cardBadge2')?.value || '').trim();
        const secondary: string[] = [];
        let primary = '';

        const normalizedBadge1 = this.normalizeUnitSecondaryBadge(badge1);
        const normalizedBadge2 = this.normalizeUnitSecondaryBadge(badge2);

        if (normalizedBadge1) secondary.push(normalizedBadge1);
        else if (badge1) primary = badge1;

        if (normalizedBadge2 && !secondary.includes(normalizedBadge2)) secondary.push(normalizedBadge2);
        else if (!primary && badge2) primary = badge2;

        return { primary, secondary };
    }

    private normalizeUnitSecondaryBadge(value: unknown): string | null {
        const normalized = String(value || '').trim().toLowerCase();
        if (!normalized) return null;
        return this.unitBadgeSecondaryOptions.find((option) => option.toLowerCase() === normalized) || null;
    }

    private maxUnitSecondaryBadges(primary: string): number {
        return String(primary || '').trim() ? 1 : 2;
    }

    private applyUnitBadgeSelection(primary: string, secondary: string[]): void {
        const normalizedPrimary = String(primary || '').trim();
        const allowedSecondary = secondary
            .map((option) => this.normalizeUnitSecondaryBadge(option))
            .filter((option): option is string => Boolean(option));
        const uniqueSecondary = [...new Set(allowedSecondary)].slice(0, this.maxUnitSecondaryBadges(normalizedPrimary));

        const badge1 = normalizedPrimary || uniqueSecondary[0] || '';
        const badge2 = normalizedPrimary ? (uniqueSecondary[0] || '') : (uniqueSecondary[1] || '');

        this.propertyForm.patchValue({ cardBadge1: badge1, cardBadge2: badge2 }, { emitEvent: false });
        this.touchDraft();
    }

    stepState(stepId: number): StepState {
        if (this.currentStep > stepId) return 'done';
        if (this.currentStep === stepId) return 'active';
        return 'pending';
    }

    fieldInvalid(controlName: string): boolean {
        const control = this.propertyForm.get(controlName);
        return !!(control && control.invalid && control.touched);
    }

    inputClasses(controlName: string): string {
        return this.fieldInvalid(controlName) ? 'field-invalid' : '';
    }

    countryClasses(): string {
        return this.fieldInvalid('country')
            ? 'block rounded-xl border border-red-400/70 p-1'
            : '';
    }

    goToStep(stepId: number): void {
        if (this.saving) return;
        const id = Number(stepId);
        if (!Number.isFinite(id)) return;
        this.currentStep = Math.min(Math.max(1, id), this.steps.length);
        this.touchDraft();
    }

    async submitAll(): Promise<void> {
        if (this.saving) return;

        this.error = null;
        this.validationSummary = [];

        const issues = this.collectValidationSummary();
        if (issues.length) {
            this.validationSummary = issues;
            this.error = 'Please complete the required fields before finishing.';
            this.propertyForm.markAllAsTouched();
            this.notifications.showError(this.error).subscribe();
            this.currentStep = 6;
            return;
        }

        const { payload, error } = this.buildPropertyPayload();
        if (!payload) {
            const message = error || 'Please fix the highlighted fields.';
            this.error = message;
            this.validationSummary = [
                {
                    stepId: 1,
                    label: this.stepLabel(1),
                    fields: [message]
                }
            ];
            this.notifications.showError(message).subscribe();
            this.currentStep = 6;
            return;
        }

        this.saving = true;
        try {
            let propertyId = (this.createdPropertyId || '').toString().trim() || null;

            if (propertyId) {
                try {
                    const updated = await firstValueFrom(
                        this.propertiesService.updateProperty(propertyId, payload).pipe(takeUntil(this.destroy$))
                    );
                    propertyId = updated?.id || propertyId;
                } catch (updateErr: any) {
                    const isNotFound =
                        updateErr?.status === 404 ||
                        String(updateErr?.error?.error?.code || '').toUpperCase() === 'NOT_FOUND' ||
                        String(updateErr?.error?.code || '').toUpperCase() === 'NOT_FOUND';
                    if (!isNotFound) throw updateErr;

                    this.notifications.showInfo('Saved draft referenced a missing property. Creating a new property...').subscribe();
                    propertyId = null;
                }
            } else {
                const created = await firstValueFrom(
                    this.propertiesService.createProperty(payload).pipe(takeUntil(this.destroy$))
                );
                propertyId = created?.id || null;
            }

            if (!propertyId) {
                const created = await firstValueFrom(
                    this.propertiesService.createProperty(payload).pipe(takeUntil(this.destroy$))
                );
                propertyId = created?.id || null;
            }

            this.createdPropertyId = propertyId;
            this.persistDraft();

            if (!propertyId) {
                throw new Error('Property saved but no ID returned from server');
            }

	            if (this.floorPlanFile) {
	                const uploaded = await firstValueFrom(
	                    this.uploadService
	                        .uploadFile(this.floorPlanFile, { isPublic: true, propertyId })
                        .pipe(takeUntil(this.destroy$))
                );
                const floorPlanFileId = uploaded?.file_id ? String(uploaded.file_id).trim() : '';
                if (!floorPlanFileId) throw new Error('Floor plan upload failed');

                await firstValueFrom(
                    this.propertiesService.updateProperty(propertyId, { floor_plan_file_id: floorPlanFileId }).pipe(takeUntil(this.destroy$))
                );
                this.floorPlanFile = null;
            }

            if (this.layoutImageFile) {
                const uploaded = await firstValueFrom(
                    this.uploadService
                        .uploadFile(this.layoutImageFile, { isPublic: true, propertyId })
                        .pipe(takeUntil(this.destroy$))
                );
                const layoutImageFileId = uploaded?.file_id ? String(uploaded.file_id).trim() : '';
                if (!layoutImageFileId) throw new Error('Layout image upload failed');

                await firstValueFrom(
                    this.propertiesService.updateProperty(propertyId, { layout_image_file_id: layoutImageFileId }).pipe(takeUntil(this.destroy$))
                );
                this.layoutImageFile = null;
            }

            for (const [featureId, value] of this.selectedFeatures.entries()) {
                await firstValueFrom(
                    this.uploadService.upsertFeatureValue(propertyId, featureId, value || 'Yes').pipe(takeUntil(this.destroy$))
                );
            }

	            const landmarkItems = (this.landmarks.getRawValue?.() || this.landmarks.value || [])
	                .map((lm: any) => {
	                    const name = String(lm?.name || '').trim();
	                    const distanceValue = this.parseNumber(lm?.distance);
			            const distanceUnit = this.normalizeDistanceUom(lm?.distanceUnit);
			            const distanceMeter = this.distanceToMeters(distanceValue, distanceUnit);
			            const timeRaw = this.parseInt(lm?.timeMinutes);
			            const timeMinutes = timeRaw !== null && timeRaw > 0 ? timeRaw : null;
			            if (!name || (distanceMeter === null && timeMinutes === null)) return null;
			            return {
			                name,
			                distance_meter: distanceMeter,
			                distance_uom: distanceUnit,
			                time_minutes: timeMinutes
			            };
			        })
			        .filter((lm: any) => Boolean(lm)) as Array<{
			            name: string;
			            distance_meter: number | null;
			            distance_uom: 'm' | 'km' | 'mile';
			            time_minutes: number | null;
			        }>;

            for (const landmark of landmarkItems) {
                await firstValueFrom(this.uploadService.createLandmark(propertyId, landmark).pipe(takeUntil(this.destroy$)));
            }

            for (let i = 0; i < this.images.length; i++) {
                if (this.destroyed) return;
                const image = this.images[i];
                const uploaded = await firstValueFrom(
                    this.uploadService
                        .uploadFile(image.file, { isPublic: true, propertyId })
                        .pipe(takeUntil(this.destroy$))
                );
                if (this.destroyed) return;
                await firstValueFrom(
                    this.uploadService
                        .attachPropertyImage(propertyId, {
                            file_id: uploaded.file_id,
                            alt: image.description || null,
                            is_main: image.isMain,
                            sort_order: i + 1
                        })
                        .pipe(takeUntil(this.destroy$))
                );
            }

            const plans = this.paymentPlans.value as Array<any>;
            const validPlans = this.planMode === 'none'
                ? []
                : plans
                    .map((plan, index) => ({ plan, index }))
                    .filter(({ plan }) =>
                        plan.paymentCycle || plan.installmentAmount || plan.numberOfInstallments || plan.totalPeriod || plan.downPayment || plan.reservationAmount || plan.displayPrice
                    );

	            const price = this.parseNumber(this.propertyForm.value.price);
            const createdPlanIds: Array<{ index: number; id: string | null }> = [];

	            for (const entry of validPlans) {
                    const { plan, index } = entry;
	                const totalPeriod = this.parseInt(plan.totalPeriod);
	                const paymentCycle = this.parseInt(plan.paymentCycle);
	                const numberOfInstallments = this.parseInt(plan.numberOfInstallments);
                if (!totalPeriod || totalPeriod <= 0) {
                    throw new Error('Plan: Total Period must be a positive number');
                }
                if (!paymentCycle || paymentCycle <= 0) {
                    throw new Error('Plan: Payment Cycle must be a positive number');
                }
                if (!numberOfInstallments || numberOfInstallments <= 0) {
                    throw new Error('Plan: Number of Installments must be a positive number');
                }

                const downPercent = this.parseNumber(plan.downPayment);
                const reservePercent = this.parseNumber(plan.reservationAmount);
                const downAmount =
                    downPercent !== null && price !== null
                        ? (price * downPercent) / 100
                        : this.parseNumber(plan.downPayment);
	                const reserveAmount =
	                    reservePercent !== null && price !== null
	                        ? (price * reservePercent) / 100
	                        : this.parseNumber(plan.reservationAmount);

	                const paymentTerms = this.buildPaymentTermsFromExtraRows(plan, price);

	                const createdPlanResponse = await firstValueFrom(
	                    this.uploadService
	                        .createPaymentPlan(propertyId, {
	                            total_period: totalPeriod,
	                            payment_cycle: paymentCycle,
	                            downpayment: downAmount ?? 0,
	                            reservation_amount: reserveAmount ?? 0,
		                            installment_amount: this.parseNumber(plan.installmentAmount),
		                            number_of_installments: numberOfInstallments,
		                            display_price: this.parseNumber(plan.displayPrice),
		                            payment_terms: paymentTerms
	                        })
	                        .pipe(takeUntil(this.destroy$))
	                );
                    createdPlanIds.push({ index, id: this.extractPaymentPlanId(createdPlanResponse) });
	            }

            await firstValueFrom(
                this.propertiesService.updateOfferDefaults(propertyId, {
                    offer_default_payment_plan_id: this.resolveSelectedPublicPricePlanIdFromCreated(createdPlanIds)
                }).pipe(takeUntil(this.destroy$))
            );

            this.finish();
        } catch (err: any) {
            if (this.isCancelled(err)) return;
            const message = err?.message || 'Failed to create property';
            this.error = message;
            this.notifications.showError(message).subscribe();
        } finally {
            this.saving = false;
            this.persistDraft();
        }
    }

    private stepLabel(stepId: number): string {
        const match = this.steps.find((s) => s.id === stepId);
        return match?.label || `Step ${stepId}`;
    }

    private collectValidationSummary(): Array<{ stepId: number; label: string; fields: string[] }> {
        const issues: Array<{ stepId: number; label: string; fields: string[] }> = [];

        const raw = this.propertyForm.getRawValue();
        const missingInfo: string[] = [];

        const title = String(raw.title || '').trim();
        if (!title) missingInfo.push('Title');

        const projectId = String(raw.projectId || '').trim();
        if (!projectId) missingInfo.push('Project');

        const phaseId = String(raw.phaseId || '').trim();
        if (!phaseId) missingInfo.push('Phase');

        const country = String(raw.country || '').trim();
        if (!country) missingInfo.push('Country');

        const currencyId = String(raw.currencyId || '').trim();
        if (!currencyId) missingInfo.push('Currency');

        const price = this.parseNumber(raw.price);
        if (price === null || price <= 0) missingInfo.push('Price');

        const unitNumber = String(raw.unitNumber || '').trim();
        if (!unitNumber && this.unitNumberRequired) missingInfo.push('Unit Number');

        const categoryId = String(raw.categoryId || '').trim();
        if (!categoryId) missingInfo.push('Property Type');

        const buaEnabled = raw.buaEnabled !== false;
        const buaVal = this.parseNumber(raw.bua);
        if (buaEnabled && (!Number.isFinite(buaVal as number) || (buaVal as number) <= 0)) {
            missingInfo.push('Built-up Area (BUA)');
        }

        const locationLink = String(raw.locationLink || '').trim();
        if (locationLink && !/^https?:\/\//i.test(locationLink)) {
            missingInfo.push('Location Link (must start with http(s)://)');
        }

        if (String(raw.discountRateChoice || '') === 'custom') {
            const rate = this.parseNumber(raw.customDiscountRate);
            if (rate === null) missingInfo.push('Custom discount rate');
        }

        if (missingInfo.length) {
            issues.push({ stepId: 1, label: this.stepLabel(1), fields: missingInfo });
        }

        const paymentIssues = this.paymentValidationIssues();
        if (paymentIssues.length) {
            issues.push({ stepId: 5, label: this.stepLabel(5), fields: paymentIssues });
        }

        return issues;
    }

    private paymentValidationIssues(): string[] {
        if (this.planMode === 'none') return [];
        const plans = this.paymentPlans.value as Array<any>;
        const validPlans = plans.filter((plan) =>
            plan.paymentCycle || plan.installmentAmount || plan.numberOfInstallments || plan.totalPeriod || plan.downPayment || plan.reservationAmount || plan.displayPrice
        );
        if (!validPlans.length) {
            const selectedPriceIssue = this.publicPriceSourceIssue();
            return selectedPriceIssue ? [selectedPriceIssue] : [];
        }

        const issues: string[] = [];
        for (let i = 0; i < validPlans.length; i++) {
            const plan = validPlans[i];
            const idx = i + 1;
            const downPct = this.parseNumber(plan.downPayment);
            const reservePct = this.parseNumber(plan.reservationAmount);
            if ((downPct !== null && (downPct < 0 || downPct > 100)) || (reservePct !== null && (reservePct < 0 || reservePct > 100))) {
                issues.push(`Plan ${idx}: Percentages must be between 0 and 100`);
            }
            if (!plan.totalPeriod) issues.push(`Plan ${idx}: Total Period`);
            if (!plan.paymentCycle) issues.push(`Plan ${idx}: Payment Cycle`);
            if (!plan.installmentAmount) issues.push(`Plan ${idx}: Installment Amount`);
            if (!plan.numberOfInstallments) issues.push(`Plan ${idx}: Number of Installments`);
        }
        const selectedPriceIssue = this.publicPriceSourceIssue();
        if (selectedPriceIssue) issues.push(selectedPriceIssue);
        return issues;
    }

    private buildPropertyPayload(): { payload: Record<string, unknown> | null; error: string | null } {
        const raw = this.propertyForm.getRawValue();

        const buaEnabled = raw.buaEnabled !== false;
        const buaVal = this.parseNumber(raw.bua);
        if (buaEnabled && (!Number.isFinite(buaVal as number) || (buaVal as number) <= 0)) {
            return { payload: null, error: 'Built-up Area must be a positive number' };
        }

        const title = String(raw.title || '').trim();
        const projectId = String(raw.projectId || '').trim();
        const phaseId = String(raw.phaseId || '').trim();
        const countryCode = String(raw.country || '').trim();

        if (!title || !projectId || !phaseId || !countryCode) {
            return { payload: null, error: 'Missing required fields in Step 1' };
        }

        const price = this.parseNumber(raw.price);
        if (price === null || price <= 0) {
            return { payload: null, error: 'Price must be a positive number' };
        }

        const currencyCode = String(raw.currencyId || '').trim();
        if (!currencyCode) {
            return { payload: null, error: 'Currency is required' };
        }

        const unitNumber = String(raw.unitNumber || '').trim();
        if (!unitNumber && this.unitNumberRequired) {
            return { payload: null, error: 'Unit number is required' };
        }

        const categoryId = String(raw.categoryId || '').trim();
        if (!categoryId) {
            return { payload: null, error: 'Property type is required' };
        }

        const rawStatus = String(raw.status || '').trim();
        const statusValue = rawStatus === 'ReservedOrHold' ? 'Reserved' : rawStatus;

	        const payload: Record<string, unknown> = {
	            title,
	            project_id: projectId,
	            phase_id: phaseId,
	            country_code: countryCode,
	            bua: buaEnabled ? buaVal : null,
	            bua_uom: String(raw.buaUnit || 'm²').trim() || 'm²',
	            total_area_uom: String(raw.totalAreaUnit || 'm²').trim() || 'm²',
	            garden_area_uom: String(raw.gardenAreaUnit || 'm²').trim() || 'm²',
	            balcony_area_uom: String(raw.balconyAreaUnit || 'm²').trim() || 'm²',
	            terrace_area_uom: String(raw.terraceAreaUnit || 'm²').trim() || 'm²',
                price,
                currency_code: currencyCode,
                unit_number: unitNumber || null,
                category_id: categoryId
	        };

        if (this.canEditStatus && statusValue) {
            payload['status'] = statusValue;
        }

        const setOptional = (key: string, value: unknown) => {
            if (value !== null && value !== undefined && value !== '') payload[key] = value;
        };

        setOptional('description', String(raw.description || '').trim());
        setOptional('tag_line', String(raw.tagLine || '').trim());

        const discountChoice = raw.discountRateChoice;
        const discountRate = discountChoice === 'custom'
            ? this.parseNumber(raw.customDiscountRate)
            : this.parseNumber(discountChoice);
        if (discountRate !== null && discountRate !== undefined) {
            payload['discount_rate'] = discountRate;
        }

        if (raw.totalAreaEnabled) setOptional('total_area', this.parseNumber(raw.totalArea));
        if (raw.gardenAreaEnabled) setOptional('garden_area', this.parseNumber(raw.gardenArea));
        if (raw.balconyAreaEnabled) setOptional('balcony_area', this.parseNumber(raw.balconyArea));
        if (raw.terraceAreaEnabled) setOptional('terrace_area', this.parseNumber(raw.terraceArea));

        setOptional('bedrooms', this.parseInt(raw.bedrooms));
        setOptional('bathrooms', this.parseInt(raw.bathrooms));
        setOptional('floor', String(raw.floor || '').trim());
        setOptional('number_of_floors', String(raw.numberOfFloors || '').trim());
        setOptional('location_address', String(raw.locationAddress || '').trim());

        const locationLink = String(raw.locationLink || '').trim();
        if (locationLink) {
            if (!/^https?:\/\//i.test(locationLink)) {
                return { payload: null, error: 'Location link must start with http(s)://' };
            }
            payload['location_link'] = locationLink;
        }

        const readyToMove = raw.readyToMove;
        if (readyToMove === 'true') payload['ready_to_move'] = true;
        if (readyToMove === 'false') payload['ready_to_move'] = false;

        setOptional('delivery_date', String(raw.deliveryDate || '').trim());
        setOptional('finishing_type', String(raw.finishingType || '').trim());
        setOptional('furnish_type', String(raw.furnishType || '').trim());
        setOptional('card_badge_1', String(raw.cardBadge1 || '').trim());
        setOptional('card_badge_2', String(raw.cardBadge2 || '').trim());
        setOptional('maintenance_fee_mode', String(raw.maintenanceFeeMode || '').trim());
        setOptional('maintenance_fees', this.parseNumber(raw.maintenanceFees));

        return { payload, error: null };
    }

    finish() {
        this.completed = true;
        this.saving = false;
        this.clearDraft();
        this.validationSummary = [];
        this.notifications.showSuccess('Property created successfully').subscribe();
    }

    startOver() {
        this.currentStep = 1;
        this.completed = false;
        this.createdPropertyId = null;
        this.propertyForm.reset({ status: this.canEditStatus ? 'Available' : 'Draft', country: '' });
        const statusControl = this.propertyForm.get('status');
        if (statusControl) {
            if (this.canEditStatus) statusControl.enable({ emitEvent: false });
            else statusControl.disable({ emitEvent: false });
        }
	        this.landmarks.clear();
	        this.landmarks.push(this.createLandmarkGroup());
	        this.paymentPlans.clear();
	        this.planMode = 'none';
	        this.resetPlanScenarios();
	        this.selectedFeatures.clear();
	        this.images = [];
	        this.floorPlanFile = null;
	        this.floorPlanError = null;
	        this.layoutImageFile = null;
	        this.layoutImageError = null;
	        this.error = null;
	        this.validationSummary = [];
        this.clearDraft();
    }

    onFloorPlanSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        input.value = '';
        this.floorPlanError = null;
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            this.floorPlanError = 'Please upload an image file.';
            this.notifications.showError(this.floorPlanError).subscribe();
            return;
        }
        this.floorPlanFile = file;
    }

    clearFloorPlan() {
        this.floorPlanFile = null;
        this.floorPlanError = null;
    }

    onLayoutImageSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        input.value = '';
        this.layoutImageError = null;
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            this.layoutImageError = 'Please upload an image file.';
            this.notifications.showError(this.layoutImageError).subscribe();
            return;
        }
        this.layoutImageFile = file;
    }

    clearLayoutImage() {
        this.layoutImageFile = null;
        this.layoutImageError = null;
    }

    prevStep() {
        if (this.saving) return;
        if (this.currentStep > 1) {
            this.currentStep -= 1;
            this.touchDraft();
        }
    }

    nextStep() {
        if (this.saving) return;
        if (this.currentStep < this.steps.length) {
            this.currentStep += 1;
            this.touchDraft();
        }
    }

    fillDummyData() {
        const projectId = this.projects[0]?.id || '';
        const currencyId = this.currencies[0]?.ROWID || '';
        const categoryId = this.categoryIdByName('Villa') || '';

        this.propertyForm.patchValue({
            title: 'Luxury Beachfront Villa',
            tagLine: 'Your dream home awaits',
            description: 'Stunning 5-bedroom villa with panoramic ocean views, private pool, and modern amenities.',
            projectId,
            phaseId: this.phases[0]?.id || '',
            country: 'QA',
            price: '1050000',
            currencyId,
            bua: '280.5',
            buaUnit: 'm²',
            totalAreaEnabled: true,
            totalArea: '300.5',
            totalAreaUnit: 'm²',
            gardenAreaEnabled: true,
            gardenArea: '50',
            gardenAreaUnit: 'm²',
            balconyAreaEnabled: true,
            balconyArea: '30',
            balconyAreaUnit: 'm²',
            terraceAreaEnabled: true,
            terraceArea: '30',
            terraceAreaUnit: 'm²',
            bedrooms: '5',
            bathrooms: '3',
            numberOfFloors: 'Ground + 1',
            floor: '2',
            unitNumber: 'B-302',
            readyToMove: 'true',
            finishingType: 'Deluxe',
            furnishType: 'Furnished',
            discountRateChoice: '15',
            maintenanceFees: '1000',
            maintenanceFeeMode: 'fixed',
            deliveryDate: '',
            categoryId
        });

        // Ensure dummy data satisfies minimum offer requirements (price + payment plan).
        this.setPlanMode('manual');
        const plan = this.paymentPlans.at(0) as FormGroup | null;
        plan?.patchValue(
            {
                totalPeriod: 72, // months
                paymentCycle: 3, // quarterly
                downPayment: 10, // %
                reservationAmount: 5, // %
                installmentAmount: 15000,
                numberOfInstallments: 24
            },
            { emitEvent: false }
        );

        if (this.canEditStatus) {
            this.propertyForm.patchValue({ status: 'Available' });
        }

        this.selectedFeatures.clear();
        this.availableFeatures.slice(0, 3).forEach((feat) => {
            this.selectedFeatures.set(feat.id, feat.dataTypeLabel === 'BOOL' ? 'true' : 'Yes');
        });
    }

    addLandmark() {
        this.landmarks.push(this.createLandmarkGroup());
    }

    removeLandmark(index: number) {
        if (this.landmarks.length === 1) return;
        this.landmarks.removeAt(index);
    }

	    applyLandmarkPreset(index: number) {
	        const group = this.landmarks.at(index) as FormGroup | undefined;
	        const preset = group?.get('type')?.value;
	        if (group && preset && preset !== 'custom') {
	            group.patchValue({ name: preset });
	        }
	    }

	    private createPlanScenarioGroup(seed?: any): FormGroup {
	        const safe = seed && typeof seed === 'object' ? seed : {};
	        return this.fb.group({
	            years: [this.parseInt(safe.years) ?? 1],
	            paymentFrequency: [safe.paymentFrequency ?? 'monthly'],
	            downPercent: [this.parseNumber(safe.downPercent) ?? 10],
	            reservationPercent: [this.parseNumber(safe.reservationPercent) ?? 5]
	        });
	    }

	    addPlanScenario(seed?: Partial<{ years: number; paymentFrequency: string; downPercent: number; reservationPercent: number }>) {
	        const last = this.planScenarios.length ? (this.planScenarios.at(this.planScenarios.length - 1)?.value as any) : {};
	        const base = {
	            years: this.parseInt(last?.years) ?? 1,
	            paymentFrequency: last?.paymentFrequency ?? 'monthly',
	            downPercent: this.parseNumber(last?.downPercent) ?? 10,
	            reservationPercent: this.parseNumber(last?.reservationPercent) ?? 5
	        };
	        this.planScenarios.push(this.createPlanScenarioGroup({ ...base, ...(seed || {}) }));
	    }

	    removePlanScenario(index: number) {
	        if (this.planScenarios.length <= 1) return;
	        this.planScenarios.removeAt(index);
	    }

	    addPlanScenarioPreset(years: number) {
	        const safeYears = Number.isFinite(years) && years > 0 ? years : 1;
	        this.addPlanScenario({ years: safeYears });
	    }

	    resetPlanScenarios(scenarios?: any[]) {
	        this.planScenarios.clear();
	        const source = Array.isArray(scenarios) && scenarios.length ? scenarios : [null];
	        source.forEach((s) => this.planScenarios.push(this.createPlanScenarioGroup(s)));
	    }

	    planScenarioPreview(index: number): string {
	        const price = this.parseNumber(this.propertyForm.value.price);
	        if (price === null || price <= 0) return 'Enter a unit price to preview.';
	        const scenario = this.planScenarios.at(index)?.value as any;
	        const computed = this.computeGeneratedPlan(scenario, price);
	        if (!computed) return '—';
	        const currency = this.selectedCurrencyCode();
	        const freqLabel = computed.paymentCycle === 12 ? 'Yearly' : computed.paymentCycle === 3 ? 'Quarterly' : 'Monthly';
	        const installmentLabel = `${this.formatNumberNoDecimals(computed.installmentAmount)}${currency ? ` ${currency}` : ''}`;
	        return `${computed.totalPeriod / 12}y • ${freqLabel} • ${computed.numberOfInstallments} installments • Installment ${installmentLabel}`;
	    }

	    private computeGeneratedPlan(scenario: any, price: number): {
	        totalPeriod: number;
	        paymentCycle: number;
	        downPct: number;
	        reservePct: number;
	        installmentAmount: number;
	        numberOfInstallments: number;
	    } | null {
	        if (!price || price <= 0) return null;
	        const safe = scenario && typeof scenario === 'object' ? scenario : {};
	        const years = this.parseInt(safe.years) || 1;
	        const frequency = (safe.paymentFrequency || 'monthly').toString();
	        const downPct = Math.max(0, Math.min(100, this.parseNumber(safe.downPercent) ?? 0));
	        const reservePct = Math.max(0, Math.min(100, this.parseNumber(safe.reservationPercent) ?? 0));
	        const paymentCycle = frequency === 'Quarterly' ? 3 : frequency === 'yearly' ? 12 : 1;
	        const totalPeriod = years * 12;
	        const numberOfInstallments = Math.max(1, Math.floor(totalPeriod / paymentCycle));
	        const downAmount = (price * downPct) / 100;
	        const reserveAmount = (price * reservePct) / 100;
	        const remaining = Math.max(0, price - downAmount - reserveAmount);
	        const installmentAmount = numberOfInstallments ? remaining / numberOfInstallments : remaining;
	        return {
	            totalPeriod,
	            paymentCycle,
	            downPct,
	            reservePct,
	            installmentAmount: Number(installmentAmount.toFixed(2)),
	            numberOfInstallments
	        };
	    }

	    setPlanMode(mode: 'manual' | 'auto' | 'none') {
	        this.planMode = mode;
	        if (mode === 'none') {
	            this.paymentPlans.clear();
                this.publicPriceSource = 'static';
        } else if (this.paymentPlans.length === 0) {
            this.paymentPlans.push(this.createPaymentPlanGroup());
        }
        this.normalizePublicPriceSourceSelection();
        this.touchDraft();
    }

	    generateAutoPlan() {
	        const price = this.parseNumber(this.propertyForm.value.price);
	        if (!price || price <= 0) {
	            this.error = 'Enter a price to generate payment plans.';
	            return;
	        }
	        this.error = null;
	        const scenarios = (this.planScenarios.getRawValue?.() as any) || [];
	        const computed = (Array.isArray(scenarios) ? scenarios : [])
	            .map((scenario: any) => this.computeGeneratedPlan(scenario, price))
	            .filter(Boolean) as Array<{
	            totalPeriod: number;
	            paymentCycle: number;
	            downPct: number;
	            reservePct: number;
	            installmentAmount: number;
	            numberOfInstallments: number;
	        }>;

	        if (!computed.length) {
	            this.error = 'Add at least one scenario to generate plans.';
	            return;
	        }

	        this.paymentPlans.clear();
	        computed.forEach((plan) => {
	            const group = this.createPaymentPlanGroup();
	            group.patchValue(
	                {
	                    totalPeriod: plan.totalPeriod,
	                    paymentCycle: plan.paymentCycle,
		                    downPayment: plan.downPct,
		                    reservationAmount: plan.reservePct,
		                    displayPrice: price,
		                    installmentAmount: plan.installmentAmount,
	                    numberOfInstallments: plan.numberOfInstallments
	                },
	                { emitEvent: false }
	            );
	            this.paymentPlans.push(group);
	        });

	        this.planMode = 'auto';
            this.normalizePublicPriceSourceSelection();
	        this.touchDraft();
	    }

    addPaymentPlan() {
        if (this.planMode === 'none') {
            this.planMode = 'manual';
        }
        this.paymentPlans.push(this.createPaymentPlanGroup());
        this.normalizePublicPriceSourceSelection();
        this.touchDraft();
    }

	    removePaymentPlan(index: number) {
	        if (this.paymentPlans.length === 1) return;
	        this.paymentPlans.removeAt(index);
            this.rebasePublicPriceSourceAfterPlanRemoval(index);
            this.touchDraft();
	    }

	    paymentPlanRows(planIndex: number): FormArray {
	        const plan = this.paymentPlans.at(planIndex) as FormGroup | null;
	        const rows = plan?.get('extraRows') as FormArray | null;
	        return rows || this.fb.array([]);
	    }

	    addPaymentPlanRow(planIndex: number) {
	        const rows = this.paymentPlanRows(planIndex);
	        if (!rows) return;
	        rows.push(this.createPaymentPlanRowGroup());
	    }

	    removePaymentPlanRow(planIndex: number, rowIndex: number) {
	        const rows = this.paymentPlanRows(planIndex);
	        if (!rows || rowIndex < 0 || rowIndex >= rows.length) return;
	        rows.removeAt(rowIndex);
	    }

	    paymentPlanRowAmountLabel(planIndex: number, rowIndex: number): string {
	        const amount = this.paymentPlanRowAmount(planIndex, rowIndex);
	        if (amount === null) return '—';
	        const currency = this.selectedCurrencyCode();
	        const formatted = this.formatNumberNoDecimals(amount);
	        return currency ? `${formatted} ${currency}` : formatted;
	    }

	    private paymentPlanRowAmount(planIndex: number, rowIndex: number): number | null {
	        const price = this.parseNumber(this.propertyForm.value.price);
	        if (price === null) return null;
	        const rows = this.paymentPlanRows(planIndex);
	        const row = rows.at(rowIndex)?.value as any;
	        const percent = this.parseNumber(row?.percent);
	        if (percent === null) return null;
	        return (price * percent) / 100;
	    }

    onImagesSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files || []);
        files.forEach((file, idx) => {
            if (!file.type.startsWith('image/')) return;
            const reader = new FileReader();
            reader.onload = (e) => {
                const preview = typeof e.target?.result === 'string' ? e.target.result : '';
                this.images.push({
                    file,
                    preview,
                    description: '',
                    isMain: this.images.length === 0 && idx === 0
                });
            };
            reader.readAsDataURL(file);
        });
        input.value = '';
    }

    setMainImage(index: number) {
        this.images = this.images.map((img, i) => ({ ...img, isMain: i === index }));
    }

    removeImage(index: number) {
        this.images.splice(index, 1);
        if (!this.images.some(img => img.isMain) && this.images[0]) {
            this.images[0].isMain = true;
        }
        this.touchDraft();
    }

    toggleFeature(feature: FeatureOption) {
        if (this.selectedFeatures.has(feature.id)) {
            this.selectedFeatures.delete(feature.id);
        } else {
            this.selectedFeatures.set(feature.id, 'Yes');
        }
        this.touchDraft();
    }

    isFeatureSelected(featureId: string): boolean {
        return this.selectedFeatures.has(featureId);
    }

    badgeFeatureAvailable(name: string): boolean {
        return Boolean(this.badgeFeatureOption(name));
    }

    isNamedFeatureSelected(name: string): boolean {
        const feature = this.badgeFeatureOption(name);
        return Boolean(feature && this.selectedFeatures.has(feature.id));
    }

    toggleNamedFeature(name: string, event: Event): void {
        const feature = this.badgeFeatureOption(name);
        const target = event.target as HTMLInputElement | null;
        if (!feature || !target) return;
        if (target.checked) this.selectedFeatures.set(feature.id, 'Yes');
        else this.selectedFeatures.delete(feature.id);
        this.touchDraft();
    }

    selectedFeatureList(): string[] {
        const names = new Map<string, string>();
        this.availableFeatures.forEach(f => names.set(f.id, f.name));
        return Array.from(this.selectedFeatures.keys()).map(id => names.get(id) || id);
    }

    private badgeFeatureOption(name: string): FeatureOption | null {
        const expected = String(name || '').trim().toLowerCase();
        if (!expected) return null;
        return this.availableFeatures.find((feature) => String(feature?.name || '').trim().toLowerCase() === expected) || null;
    }

    onProjectChange() {
        const projectId = this.propertyForm.value.projectId;
        if (!projectId) {
            this.phases = [];
            this.propertyForm.patchValue({ phaseId: '' });
            this.syncLocationControls(this.useProjectLocation);
            return;
        }
        const rows = (this.allPhases || []).filter((phase) => String(phase?.project_id || '') === String(projectId));
        this.phases = rows;
        const desired = this.propertyForm.value.phaseId || '';
        const match = rows.find((p) => p.id === desired);
        this.propertyForm.patchValue({ phaseId: match ? match.id : (rows[0]?.id || '') });
        this.syncLocationControls(this.useProjectLocation);
    }

    goToProperties() {
        this.router.navigate(['/developer/properties']);
    }

    selectedCurrencyCode(): string {
        const match = this.currencies.find(c => c.ROWID === this.propertyForm.value.currencyId);
        return match?.CODE || match?.NAME || '';
    }

		    private createLandmarkGroup(): FormGroup {
		        return this.fb.group({
		            type: ['custom'],
		            name: [''],
	                distance: [''],
	                distanceUnit: ['m'],
	                timeMinutes: ['']
		        });
		    }

			    private normalizeDistanceUom(value: unknown): 'm' | 'km' | 'mile' {
			        const raw = String(value ?? '').trim().toLowerCase();
			        if (raw === 'km' || raw === 'kilometer' || raw === 'kilometre') return 'km';
			        if (raw === 'mile' || raw === 'mi') return 'mile';
			        return 'm';
			    }

			    private distanceToMeters(value: number | null, unit: 'm' | 'km' | 'mile'): number | null {
			        if (value === null || value === undefined) return null;
			        if (!Number.isFinite(value)) return null;
			        if (unit === 'km') return Math.round(value * 1000);
			        if (unit === 'mile') return Math.round(value * 1609.344);
			        return Math.round(value);
			    }

	    private createPaymentPlanRowGroup(): FormGroup {
	        return this.fb.group({
	            label: [''],
	            percent: ['']
	        });
	    }

	    private createPaymentPlanGroup(): FormGroup {
	        return this.fb.group({
	            totalPeriod: [''],
	            paymentCycle: [''],
		            downPayment: [''],
		            reservationAmount: [''],
		            displayPrice: [''],
		            installmentAmount: [''],
	            numberOfInstallments: [''],
	            extraRows: this.fb.array([])
	        });
	    }

    private loadLookups() {
        this.propertiesService.getPropertyCreatePayload().subscribe({
            next: async (payload) => {
                try {
                    const projectRows = payload.projects || [];
                    const phaseRows = payload.phases || [];

                    this.projects = projectRows.map((proj: any) => ({
                        id: String(proj?.ROWID || proj?.row_id || proj?.id || ''),
                        name: proj?.NAME || proj?.Title || proj?.TITLE || proj?.name || 'Untitled',
                        locationAddress: proj?.LOCATION_ADDRESS ?? proj?.location_address ?? proj?.locationAddress ?? null,
                        locationLink: proj?.LOCATION_LINK ?? proj?.location_link ?? proj?.locationLink ?? null,
                        images: []
                    }));

                    this.allPhases = phaseRows.map((phase: any) => ({
                        id: String(phase?.ROWID || phase?.row_id || phase?.id || ''),
                        name: phase?.NAME || phase?.name || 'Unnamed phase',
                        status: phase?.STATUS || phase?.status || null,
                        project_id: String(phase?.PROJECT_ID || phase?.project_id || '')
                    }));

                    this.categories = payload.categories || [];
                    this.propertyTypeSelectOptions = this.buildPropertyTypeSelectOptions(this.categories);
                    this.syncUnitNumberValidation();
                    this.currencies = payload.currencies || [];
                    const policy = payload.policy || {};
                    this.canEditStatus = Boolean(policy.can_edit_status ?? policy.canEditStatus);
                    const statusControl = this.propertyForm.get('status');
                    if (statusControl) {
                        if (this.canEditStatus) {
                            statusControl.enable({ emitEvent: false });
                        } else {
                            statusControl.setValue('Draft', { emitEvent: false });
                            statusControl.disable({ emitEvent: false });
                        }
                    }
                    this.availableFeatures = (payload.features || []).map((row: any) => ({
                        id: row.ROWID || row.id,
                        name: row.NAME || row.name,
                        dataTypeLabel: row.DATA_TYPE || row.dataType || 'Text'
                    }));

                    await this.maybeRestoreDraft();
                    this.syncUnitNumberValidation();

                    const currentProjectId = String(this.propertyForm.value.projectId || '').trim();
                    if (!currentProjectId) {
                        const preferred = this.presetProjectId ? this.projects.find(p => p.id === this.presetProjectId) : undefined;
                        const projectId = preferred?.id ?? this.projects[0]?.id ?? '';
                        if (projectId) {
                            this.propertyForm.patchValue({ projectId }, { emitEvent: false });
                            this.onProjectChange();
                        }
                    }

                    const country = this.propertyForm.value.country;
                    const applied = this.applyCountryCurrency(country);
                    if (!applied && this.currencies.length && !this.propertyForm.value.currencyId) {
                        this.propertyForm.patchValue({ currencyId: this.currencies[0].ROWID }, { emitEvent: false });
                    }
                } catch (err: any) {
                    const message =
                        err?.error?.error?.message
                        || err?.error?.message
                        || err?.message
                        || 'Failed to load property setup data';
                    this.error = message;
                    this.notifications.showError(message).subscribe();
                    this.projects = [];
                    this.allPhases = [];
                    this.phases = [];
                }
            },
            error: (err) => {
                const message =
                    err?.error?.error?.message
                    || err?.error?.message
                    || err?.message
                    || 'Failed to load property setup data';
                this.error = message;
                this.notifications.showError(message).subscribe();
                this.projects = [];
                this.allPhases = [];
                this.phases = [];
                this.categories = [];
                this.propertyTypeSelectOptions = this.buildPropertyTypeSelectOptions([]);
                this.currencies = [];
                this.availableFeatures = [];
            }
        });
    }

	    private parseNumber(value: any): number | null {
	        if (value === null || value === undefined || value === '') return null;
	        const parsed = parseFloat(value);
	        return Number.isFinite(parsed) ? parsed : null;
	    }

	    private formatPercent(value: number): string {
	        return value.toFixed(2).replace(/\.?0+$/, '');
	    }

	    private buildPaymentTermsFromExtraRows(plan: any, price: number | null): string | null {
	        const rows = Array.isArray(plan?.extraRows) ? plan.extraRows : [];
	        if (!rows.length) return null;

	        const currency = this.selectedCurrencyCode();
	        const lines = rows
	            .map((row: any) => {
	                if (!row || typeof row !== 'object') return null;
	                const label = String(row.label || '').trim();
	                const percent = this.parseNumber(row.percent);
	                if (!label || percent === null) return null;
	                const amount = price !== null ? (price * percent) / 100 : null;
	                const amountLabel = amount !== null ? `${this.formatNumberNoDecimals(amount)}${currency ? ` ${currency}` : ''}` : null;
	                const pctLabel = `(${this.formatPercent(percent)}%)`;
	                return amountLabel ? `${label}: ${pctLabel} ${amountLabel}` : `${label}: ${pctLabel}`;
	            })
	            .filter(Boolean) as string[];

	        return lines.length ? lines.join('\n') : null;
	    }

    formatNumberNoDecimals(value: any): string {
        const parsed = this.parseNumber(value);
        if (parsed === null) return '—';
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(parsed);
    }

    publicPriceSourceOptions(): Array<{ value: string; label: string }> {
        const options: Array<{ value: string; label: string }> = [];
        const staticLabel = this.publicPriceAmountLabel(this.parseNumber(this.propertyForm.get('price')?.value));
        options.push({
            value: 'static',
            label: staticLabel ? `Static unit price (${staticLabel})` : 'Static unit price'
        });

        const plans = (this.paymentPlans.getRawValue?.() as Array<any>) || [];
        plans.forEach((plan, index) => {
            const displayPrice = this.publicPriceAmountLabel(this.parseNumber(plan?.displayPrice));
            options.push({
                value: this.paymentPlanPublicSourceValue(plan, index),
                label: displayPrice
                    ? `Plan ${index + 1} display price (${displayPrice})`
                    : `Plan ${index + 1} display price`
            });
        });
        return options;
    }

    setPublicPriceSource(value: any): void {
        this.publicPriceSource = this.normalizePublicPriceSource(value);
        this.normalizePublicPriceSourceSelection();
        this.touchDraft();
    }

    publicPriceSourceHelpText(): string {
        const issue = this.publicPriceSourceIssue();
        if (issue) return issue;
        if (this.publicPriceSource === 'static') {
            return 'Public cards and property pages will use the unit price field.';
        }
        const selected = this.getSelectedPublicPricePlan(this.paymentPlans.getRawValue?.() as Array<any>);
        if (!selected) {
            return 'Select a payment plan if you want the public listing price to come from a plan display price.';
        }
        const amount = this.publicPriceAmountLabel(this.parseNumber(selected.plan?.displayPrice));
        return amount
            ? `Public cards and property pages will use ${amount} from Plan ${selected.index + 1}.`
            : `Public cards and property pages will use the selected plan once its display price is set.`;
    }

    publicPriceSourceHasIssue(): boolean {
        return Boolean(this.publicPriceSourceIssue());
    }

    private publicPriceSourceIssue(): string | null {
        if (this.planMode === 'none' || this.publicPriceSource === 'static') return null;
        const selected = this.getSelectedPublicPricePlan(this.paymentPlans.getRawValue?.() as Array<any>);
        if (!selected) {
            return 'Select a valid payment plan for the public listing price or switch back to the static unit price.';
        }
        if (this.parseNumber(selected.plan?.displayPrice) === null) {
            return `Plan ${selected.index + 1} is selected for the public listing price. Enter its Public display price first.`;
        }
        return null;
    }

    private publicPriceAmountLabel(value: number | null): string {
        if (value === null) return '';
        const formatted = this.formatNumberNoDecimals(value);
        const currency = this.selectedCurrencyCode();
        return currency ? `${formatted} ${currency}` : formatted;
    }

    private paymentPlanPublicSourceValue(plan: any, index: number): string {
        const planId = String(plan?.id || '').trim();
        return planId ? `plan:${planId}` : `index:${index}`;
    }

    private normalizePublicPriceSource(value: any): string {
        const raw = String(value || '').trim();
        return raw || 'static';
    }

    private getSelectedPublicPricePlan(plans: Array<any> | null | undefined): { plan: any; index: number } | null {
        const source = this.normalizePublicPriceSource(this.publicPriceSource);
        if (source === 'static') return null;
        const list = Array.isArray(plans) ? plans : [];
        if (source.startsWith('plan:')) {
            const planId = source.slice(5);
            const index = list.findIndex((plan) => String(plan?.id || '').trim() === planId);
            return index >= 0 ? { plan: list[index], index } : null;
        }
        if (source.startsWith('index:')) {
            const index = Number.parseInt(source.slice(6), 10);
            return Number.isInteger(index) && index >= 0 && index < list.length ? { plan: list[index], index } : null;
        }
        return null;
    }

    private normalizePublicPriceSourceSelection(): void {
        if (this.planMode === 'none') {
            this.publicPriceSource = 'static';
            return;
        }
        if (this.publicPriceSource === 'static') return;
        const selected = this.getSelectedPublicPricePlan(this.paymentPlans.getRawValue?.() as Array<any>);
        if (!selected) {
            this.publicPriceSource = 'static';
        }
    }

    private rebasePublicPriceSourceAfterPlanRemoval(removedIndex: number): void {
        const source = this.normalizePublicPriceSource(this.publicPriceSource);
        if (!source.startsWith('index:')) {
            this.normalizePublicPriceSourceSelection();
            return;
        }
        const currentIndex = Number.parseInt(source.slice(6), 10);
        if (!Number.isInteger(currentIndex)) {
            this.publicPriceSource = 'static';
            return;
        }
        if (currentIndex === removedIndex) {
            this.publicPriceSource = 'static';
            return;
        }
        if (currentIndex > removedIndex) {
            this.publicPriceSource = `index:${currentIndex - 1}`;
        }
        this.normalizePublicPriceSourceSelection();
    }

    private resolveSelectedPublicPricePlanIdFromCreated(createdPlans: Array<{ index: number; id: string | null }>): string | null {
        const selected = this.getSelectedPublicPricePlan(this.paymentPlans.getRawValue?.() as Array<any>);
        if (!selected) return null;
        return createdPlans.find((entry) => entry.index === selected.index)?.id || null;
    }

    private extractPaymentPlanId(response: any): string | null {
        const directId = response?.ROWID || response?.rowId || response?.id || null;
        if (directId) return String(directId);
        const data = response?.data || response?.record || null;
        const nestedId = data?.ROWID || data?.rowId || data?.id || null;
        return nestedId ? String(nestedId) : null;
    }

    get unitNumberRequired(): boolean {
        const categoryId = String(this.propertyForm.get('categoryId')?.value || '').trim();
        const category = (this.categories || []).find(c => String(c.ROWID || '').trim() === categoryId);
        return String(category?.NAME || '').trim().toLowerCase() !== 'hospitality';
    }

    private syncUnitNumberValidation(): void {
        const control = this.propertyForm.get('unitNumber');
        if (this.unitNumberRequired) control?.addValidators(Validators.required);
        else control?.removeValidators(Validators.required);
        control?.updateValueAndValidity({ emitEvent: false });
    }

    private buildPropertyTypeSelectOptions(categories: LookupRecord[]): PropertyTypeSelectOption[] {
        const options = (categories || [])
            .map((cat) => {
                const label = (cat?.NAME ?? '').toString().trim();
                const value = (cat?.ROWID ?? '').toString().trim();
                if (!label || !value) return null;
                return { label, value };
            })
            .filter(Boolean) as Array<{ label: string; value: string }>;

        return [{ label: 'Select property type', value: '', disabled: true }, ...options];
    }

    private categoryIdByName(name: string): string | null {
        const desired = name.toString().trim().toLowerCase();
        if (!desired) return null;
        const match = (this.categories || []).find((c) => (c?.NAME || '').toString().trim().toLowerCase() === desired);
        const id = (match?.ROWID || '').toString().trim();
        return id || null;
    }

    private applyCountryCurrency(country: string | null | undefined, force = false): boolean {
        const normalized = (country || '').toString().trim().toUpperCase();
        if (!normalized || !this.currencies.length) return false;
        if (this.currencyChangedByUser && !force) return false;
        const desired = COUNTRY_CURRENCY_MAP[normalized];
        if (!desired) return false;
        const match = this.currencies.find((c) => (c.CODE || c.NAME || '').toString().toUpperCase() === desired);
        if (match) {
            this.updatingCurrencyFromCountry = true;
            this.propertyForm.patchValue({ currencyId: match.ROWID }, { emitEvent: false });
            this.updatingCurrencyFromCountry = false;
            return true;
        }
        return false;
    }

    private initAreaToggleWatchers() {
        ([
            ['buaEnabled', 'bua'],
            ['totalAreaEnabled', 'totalArea'],
            ['gardenAreaEnabled', 'gardenArea'],
            ['balconyAreaEnabled', 'balconyArea'],
            ['terraceAreaEnabled', 'terraceArea']
        ] as Array<[string, string]>).forEach(([flag, field]) => {
            this.propertyForm.get(flag)?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.onAreaToggle(flag, field));
            this.onAreaToggle(flag, field);
        });
    }

    onAreaToggle(flagControl: string, targetControl: string) {
        const enabled = this.propertyForm.get(flagControl)?.value !== false;
        const control = this.propertyForm.get(targetControl);
        if (!control) return;
        if (enabled) {
            control.enable({ emitEvent: false });
        } else {
            control.disable({ emitEvent: false });
            control.reset('');
        }
    }

    private parseInt(value: any): number | null {
        if (value === null || value === undefined || value === '') return null;
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private isCancelled(err: any): boolean {
        if (this.destroyed) return true;
        const name = String(err?.name || '').toLowerCase();
        const msg = String(err?.message || '').toLowerCase();
        return name.includes('emptyerror') || msg.includes('no elements in sequence');
    }

    private readDraft(): PropertyCreateDraftV1 | null {
        if (typeof window === 'undefined') return null;
        const maxAgeMs = 1000 * 60 * 60 * 24 * 30; // 30 days

        const parseFromStorage = (storage: Storage, migrateToLocal = false): PropertyCreateDraftV1 | null => {
            const raw = storage.getItem(this.draftKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed || parsed.v !== 1) return null;

            const savedAt = Date.parse(String(parsed.saved_at || ''));
            if (!Number.isFinite(savedAt)) return null;
            const ageMs = Date.now() - savedAt;
            if (ageMs > maxAgeMs) {
                storage.removeItem(this.draftKey);
                return null;
            }

            if (migrateToLocal) {
                try {
                    window.localStorage.setItem(this.draftKey, raw);
                    storage.removeItem(this.draftKey);
                } catch {
                    // ignore migration failures
                }
            }

            return parsed as PropertyCreateDraftV1;
        };

        try {
            const local = parseFromStorage(window.localStorage);
            if (local) return local;
        } catch {
            // ignore local storage failures
        }

        try {
            return parseFromStorage(window.sessionStorage, true);
        } catch {
            return null;
        }
    }

    private applyDraft(draft: PropertyCreateDraftV1): void {
        this.draftTouched = true;
        try {
            this.createdPropertyId = draft.created_property_id || null;
            const step = Number(draft.current_step ?? 1);
            this.currentStep = Number.isFinite(step) && step >= 1 && step <= this.steps.length ? step : 1;

	            if (draft.property_form && typeof draft.property_form === 'object') {
	                this.propertyForm.patchValue(draft.property_form, { emitEvent: false });
	                if (!Object.prototype.hasOwnProperty.call(draft.property_form, 'useProjectLocation')) {
	                    const addr = String((draft.property_form as any)?.locationAddress || '').trim();
	                    const link = String((draft.property_form as any)?.locationLink || '').trim();
	                    if (addr || link) {
	                        this.propertyForm.patchValue({ useProjectLocation: false }, { emitEvent: false });
	                    }
	                }
	            }
	            if (draft.plan_mode) this.planMode = draft.plan_mode;
                this.publicPriceSource = this.normalizePublicPriceSource(draft.public_price_source ?? 'static');
	            if (draft.plan_generator) {
	                const raw = draft.plan_generator;
	                const scenarios = (() => {
	                    if (!raw || typeof raw !== 'object') return [];
	                    if (Array.isArray((raw as any).scenarios)) return (raw as any).scenarios;
	                    // Legacy draft shape (single scenario stored as a flat object).
	                    if (
	                        'years' in (raw as any) ||
	                        'paymentFrequency' in (raw as any) ||
	                        'downPercent' in (raw as any) ||
	                        'reservationPercent' in (raw as any)
	                    ) {
	                        return [raw];
	                    }
	                    return [];
	                })();
	                this.resetPlanScenarios(scenarios);
	            }

            this.selectedFeatures.clear();
            (draft.selected_features || []).forEach(([k, v]) => {
                if (!k) return;
                this.selectedFeatures.set(String(k), String(v ?? 'Yes'));
            });

            this.landmarks.clear();
            const lms = Array.isArray(draft.landmarks) ? draft.landmarks : [];
            (lms.length ? lms : [null]).forEach((lm: any) => {
                const group = this.createLandmarkGroup();
                if (lm && typeof lm === 'object') {
                    group.patchValue(
                        {
                            type: lm.type ?? 'custom',
                            name: lm.name ?? '',
                            distance: lm.distance ?? '',
                            distanceUnit: lm.distanceUnit ?? 'm'
                        },
                        { emitEvent: false }
                    );
                }
                this.landmarks.push(group);
            });

	            this.paymentPlans.clear();
	            const restoredPlanMode = draft.plan_mode || this.planMode || 'none';
	            const plans = Array.isArray(draft.payment_plans) ? draft.payment_plans : [];
	            if (restoredPlanMode !== 'none') {
                const source = plans.length ? plans : [null];
                source.forEach((pl: any) => {
                    const group = this.createPaymentPlanGroup();
                    if (pl && typeof pl === 'object') {
	                        group.patchValue(
	                            {
	                                totalPeriod: pl.totalPeriod ?? '',
	                                paymentCycle: pl.paymentCycle ?? '',
		                                downPayment: pl.downPayment ?? '',
		                                reservationAmount: pl.reservationAmount ?? '',
		                                displayPrice: pl.displayPrice ?? '',
		                                installmentAmount: pl.installmentAmount ?? '',
	                                numberOfInstallments: pl.numberOfInstallments ?? ''
	                            },
	                            { emitEvent: false }
	                        );

	                        const extraRows = Array.isArray(pl.extraRows) ? pl.extraRows : [];
	                        const rowsArray = group.get('extraRows') as FormArray | null;
	                        if (rowsArray) {
	                            rowsArray.clear();
	                            extraRows.forEach((row: any) => {
	                                if (!row || typeof row !== 'object') return;
	                                const rowGroup = this.createPaymentPlanRowGroup();
	                                rowGroup.patchValue(
	                                    {
	                                        label: row.label ?? '',
	                                        percent: row.percent ?? ''
	                                    },
	                                    { emitEvent: false }
	                                );
	                                rowsArray.push(rowGroup);
	                            });
	                        }
	                    }
	                    this.paymentPlans.push(group);
	                });
	            }
                this.normalizePublicPriceSourceSelection();

            this.onProjectChange();
            const draftCurrency = String(draft.property_form?.currencyId || '').trim().toUpperCase();
            const explicitCurrency = this.currencies.find(currency =>
                String(currency.ROWID || '').trim().toUpperCase() === draftCurrency);
            if (draftCurrency && explicitCurrency) {
                this.propertyForm.patchValue({ currencyId: explicitCurrency.ROWID }, { emitEvent: false });
                this.currencyChangedByUser = true;
            } else {
                this.currencyChangedByUser = false;
                this.applyCountryCurrency(this.propertyForm.value.country, true);
            }
            this.syncUnitNumberValidation();
            this.persistDraft();
        } catch {
            // Ignore draft restore failures.
        }
    }

    private async maybeRestoreDraft(): Promise<void> {
        if (this.draftRestoreHandled) return;
        const draft = this.pendingDraft;
        if (!draft) {
            this.draftRestoreHandled = true;
            return;
        }

        const savedAt = (() => {
            const parsed = Date.parse(String(draft.saved_at || ''));
            if (!Number.isFinite(parsed)) return null;
            return new Date(parsed);
        })();

        const savedAtLabel = savedAt ? savedAt.toLocaleString() : null;
        const ok = await this.confirm.confirm({
            title: 'Restore draft?',
            message: savedAtLabel
                ? `We found a saved draft from ${savedAtLabel}. Restore it?`
                : 'We found a saved draft from your previous session. Restore it?',
            confirmText: 'Restore',
            cancelText: 'Discard'
        });

        this.draftRestoreHandled = true;
        this.pendingDraft = null;

        if (!ok) {
            this.clearDraft();
            this.notifications.showInfo('Draft discarded').subscribe();
            return;
        }

        this.applyDraft(draft);
        this.notifications.showInfo('Draft restored').subscribe();
    }

    private touchDraft(): void {
        this.draftTouched = true;
        this.persistDraft();
    }

    private persistDraft(): void {
        if (typeof window === 'undefined') return;
        if (this.pendingDraft && !this.draftRestoreHandled) return;
        if (!this.draftTouched) return;
        if (this.completed) {
            this.clearDraft();
            return;
        }
        try {
            const draft: PropertyCreateDraftV1 = {
                v: 1,
                saved_at: new Date().toISOString(),
                current_step: this.currentStep,
                created_property_id: this.createdPropertyId || null,
                plan_mode: this.planMode,
                public_price_source: this.publicPriceSource,
                plan_generator: this.planGeneratorForm.getRawValue(),
                property_form: this.propertyForm.getRawValue(),
                selected_features: Array.from(this.selectedFeatures.entries()),
                landmarks: (this.landmarksForm.get('items') as FormArray)?.getRawValue?.() ?? [],
                payment_plans: (this.paymentPlansForm.get('plans') as FormArray)?.getRawValue?.() ?? []
            };
            window.localStorage.setItem(this.draftKey, JSON.stringify(draft));
            this.hasSavedDraft = true;
        } catch {
            // ignore storage failures
        }
    }

    private clearDraft(): void {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.removeItem(this.draftKey);
            window.sessionStorage.removeItem(this.draftKey);
            this.hasSavedDraft = false;
        } catch {
            // ignore
        }
    }

    async clearSavedDraft(): Promise<void> {
        if (this.saving) return;
        const ok = await this.confirm.confirm({
            title: 'Clear saved draft?',
            message: 'This removes the saved draft from this device. It will not delete any property on the server.',
            confirmText: 'Clear',
            cancelText: 'Cancel',
            tone: 'danger'
        });
        if (!ok) return;

        this.clearDraft();
        this.draftTouched = false;
        this.notifications.showInfo('Draft cleared').subscribe();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.persistDraft();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
